import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarDays, Hammer, Wallet, Target, Mic, Sparkles, Users, ShieldCheck, MessagesSquare, ClipboardList, LogOut, Moon, Sun, Zap, WifiOff, RefreshCw } from "lucide-react";
import { useOffline } from "@/hooks/useOffline";
import { useMode } from "@/lib/mode";
import Birdie from "@/components/Birdie";
import HeyHeader from "@/components/HeyHeader";
import ModeSwitch from "@/components/ModeSwitch";

const BASE_TABS = [
  { to: "/", label: "Accueil", testid: "leads", icon: Target },
  { to: "/dashboard", label: "Prospects", testid: "prospects", icon: Users },
  { to: "/reunions", label: "Réunions", testid: "reunions", icon: ClipboardList },
  { to: "/equipe", label: "Équipe", testid: "equipe", icon: MessagesSquare },
];
const GESTION_TABS = [
  { to: "/", label: "Accueil", testid: "leads", icon: Target },
  { to: "/quotidien", label: "Planning", testid: "planning", icon: CalendarDays },
  { to: "/chantiers", label: "Projets", testid: "chantiers", icon: Hammer },
  { to: "/tresorerie", label: "Trésorerie", testid: "tresorerie", icon: Wallet },
];
const ATELIER_ROUTES = ["/quotidien", "/chantiers", "/tresorerie"];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useMode();
  const [dark, setDark] = useState(() => localStorage.getItem("klt_theme") !== "light");
  const gestion = mode === "atelier";
  const salon = !gestion;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("klt_theme", dark ? "dark" : "light");
  }, [dark]);
  useEffect(() => {
    if (!ATELIER_ROUTES.includes(location.pathname) || user === null) return;
    if (mode !== "atelier") setMode("atelier");
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const navItems = gestion ? GESTION_TABS : BASE_TABS;
  const { online, pending, syncing, doSync } = useOffline();
  const isActive = (to) => (to === "/" ? location.pathname === "/" : to === "/dashboard" ? location.pathname === "/dashboard" || location.pathname.startsWith("/lead") : location.pathname.startsWith(to));
  const cta = () => {
    if (salon) { window.dispatchEvent(new CustomEvent("birdie:close")); navigate("/", { state: { voice: Date.now() } }); }
    else window.dispatchEvent(new CustomEvent("birdie:dictate"));
  };
  const CtaIcon = salon ? Sparkles : Mic;

  return (
    <div className="klt-app min-h-screen bg-background flex flex-col" data-mode={mode}>
      <header className="sticky top-0 z-40 klt-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <button onClick={() => navigate("/")} data-testid="brand-logo" className="flex items-center gap-2.5 group shrink-0">
            <div className="w-9 h-9 rounded-xl klt-fab-neon flex items-center justify-center"><Zap className="w-5 h-5 text-white" fill="currentColor" /></div>
            <div className="text-left leading-none hidden min-[400px]:block">
              <div className="font-heading font-extrabold text-base tracking-tight">KLT</div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#60A5FA]">Lead Capture</div>
            </div>
          </button>

          <nav className="hidden md:flex items-center gap-1 bg-secondary/60 rounded-full p-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button key={item.to} data-testid={`nav-${item.testid}`} onClick={() => navigate(item.to)}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${isActive(item.to) ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="w-4 h-4" /> {item.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-1">
            <button onClick={cta} data-testid="header-ia-btn" className="hidden md:inline-flex items-center gap-2 h-9 px-4 rounded-full klt-fab-neon text-sm font-semibold hover:opacity-90 transition-opacity mr-1">
              <CtaIcon className="w-4 h-4" /> {salon ? "Débrief vocal" : "Note vocale"}
            </button>
            <ModeSwitch />
            {gestion && <Button variant="ghost" size="icon" onClick={() => navigate("/equipe")} data-testid="header-chat-btn" title="Discussion d'équipe" className="rounded-full"><MessagesSquare className="w-4 h-4" /></Button>}
            <Button variant="ghost" size="icon" onClick={() => setDark((d) => !d)} data-testid="theme-toggle" className="rounded-full">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            {user?.isPlatformAdmin && (
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="nav-admin" title="Administration" className="rounded-full text-amber-400"><ShieldCheck className="w-4 h-4" /></Button>
            )}
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" className="rounded-full">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full">
        {(!online || pending > 0) && (
          <div data-testid="offline-banner" className={`border-b ${online ? "bg-amber-500/10 border-amber-500/20 text-amber-700 dark:text-amber-300" : "bg-slate-500/15 border-slate-500/30 text-slate-700 dark:text-slate-200"}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 text-sm font-medium flex items-center justify-center gap-3 text-center flex-wrap">
              {!online && (<span className="flex items-center gap-1.5"><WifiOff className="w-4 h-4" /> Mode hors ligne — vos captures sont enregistrées sur l'appareil</span>)}
              {pending > 0 && (
                <span className="flex items-center gap-2" data-testid="pending-count">
                  {pending} fiche{pending > 1 ? "s" : ""} en attente de synchronisation
                  {online && (
                    <button onClick={doSync} disabled={syncing} data-testid="sync-now-button" className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2.5 py-0.5 text-xs font-semibold disabled:opacity-60">
                      <RefreshCw className={`w-3 h-3 ${syncing ? "klt-spin" : ""}`} /> Synchroniser
                    </button>
                  )}
                </span>
              )}
            </div>
          </div>
        )}
        {user?.onTrial && (
          <div data-testid="trial-banner" className="bg-emerald-500/10 border-b border-emerald-500/20 text-emerald-700 dark:text-emerald-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 text-sm font-medium flex items-center justify-center gap-2 text-center flex-wrap">
              🎁 Essai gratuit — <b>{user.trialDaysLeft} jour{user.trialDaysLeft > 1 ? "s" : ""} restant{user.trialDaysLeft > 1 ? "s" : ""}</b>.
              <button onClick={() => navigate("/abonnement")} data-testid="trial-subscribe-link" className="underline font-semibold hover:opacity-80">S'abonner maintenant</button>
            </div>
          </div>
        )}
        {user?.subscriptionRequired ? (
          <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="subscription-paywall">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/15 flex items-center justify-center mx-auto"><ShieldCheck className="w-7 h-7 text-amber-600" /></div>
            <h1 className="font-heading text-2xl font-extrabold mt-4">Votre essai gratuit est terminé</h1>
            <p className="text-muted-foreground mt-2 text-sm">Abonnez-vous pour continuer à utiliser Kallitag. Paiement immédiat, sans période d'essai, résiliable à tout moment.</p>
            <Button onClick={() => navigate("/abonnement")} data-testid="paywall-subscribe-button" className="mt-6 h-12 rounded-xl px-8 font-semibold"><Zap className="w-4 h-4 mr-2" fill="currentColor" /> Voir les offres &amp; s'abonner</Button>
          </div>
        ) : (
          <>
            <HeyHeader />
            <Outlet />
          </>
        )}
      </main>

      <nav className={`md:hidden sticky bottom-0 z-40 grid grid-cols-5 items-end bg-card/95 dark:bg-[#0A0F1D]/95 backdrop-blur-xl rounded-t-3xl border-t border-[#3B82F6]/15 pb-[env(safe-area-inset-bottom)]`} data-testid="bottom-nav">
        {navItems.slice(0, 2).map((item) => <BottomTab key={item.to} item={item} active={isActive(item.to)} onClick={() => navigate(item.to)} />)}
        <div className="flex flex-col items-center">
          <button onClick={cta} data-testid="bottomnav-ia" aria-label={salon ? "Débrief vocal IA" : "Note vocale / Créer un devis"}
            className="-mt-7 w-16 h-16 rounded-full klt-fab-neon flex items-center justify-center active:scale-95 transition-transform">
            <CtaIcon className="w-6 h-6" />
          </button>
          <span className="text-[10px] font-semibold text-slate-400 mt-1 mb-2" data-testid="bottomnav-ia-label">{salon ? "Débrief" : "Note vocale"}</span>
        </div>
        {navItems.slice(2).map((item) => <BottomTab key={item.to} item={item} active={isActive(item.to)} onClick={() => navigate(item.to)} />)}
      </nav>

      <Birdie />
    </div>
  );
}

function BottomTab({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <button data-testid={`bottomnav-${item.testid}`} onClick={onClick}
      className={`relative flex flex-col items-center gap-1 py-3 text-[11px] font-medium transition-colors ${active ? "text-[#2563EB] dark:text-[#93C5FD]" : "text-slate-500 hover:text-slate-300"}`}>
      {active && <span className="absolute top-0 w-8 h-0.5 rounded-full bg-[#3B82F6] shadow-[0_0_10px_#3B82F6]" />}
      <Icon className="w-5 h-5" /> {item.label}
    </button>
  );
}
